import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend } from 'recharts';
import { CalendarBlank, Export, CaretUp, CaretDown, X, MapPin } from '@phosphor-icons/react';

const PALETTE = ['#E04B2A', '#2B6EF0', '#E5A100', '#10A882', '#8B5CF6', '#06B6D4', '#F97316', '#EC4899', '#14B8A6'];
const fmt = (n) => `Rs.${(n || 0).toLocaleString('en-IN')}`;

export default function ReportsPage() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [fin, setFin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [counselorSort, setCounselorSort] = useState({ key: 'total_leads', dir: 'desc' });
  const [campaignPerf, setCampaignPerf] = useState([]);
  const [platformPerf, setPlatformPerf] = useState([]);
  const [campaignStatusFilter, setCampaignStatusFilter] = useState('all');
  const [campaignSort, setCampaignSort] = useState({ key: 'leads', dir: 'desc' });
  const [funnelCampaign, setFunnelCampaign] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [outOfLocation, setOutOfLocation] = useState([]);
  const [oolLoading, setOolLoading] = useState(false);
  const [counselorDetail, setCounselorDetail] = useState([]);
  const [counselorDetailLoading, setCounselorDetailLoading] = useState(false);
  const [expandedCounselorId, setExpandedCounselorId] = useState(null);
  const [counselorPanelView, setCounselorPanelView] = useState('date');
  const { theme } = useTheme();
  const navigate = useNavigate();

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    Promise.all([
      api.get('/api/analytics/dashboard', { params }),
      api.get('/api/analytics/finance', { params }),
    ]).then(([d, f]) => { setDash(d.data); setFin(f.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Counselor date-wise / by-course breakdown (ADM-6 #14). Reuses the same
  // header date-range picker as the rest of the page -- no second picker.
  // When the selected range exceeds ~31 days, auto-switch to weekly buckets
  // so the "By Date" table doesn't explode to hundreds of daily rows.
  useEffect(() => {
    setCounselorDetailLoading(true);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (startDate && endDate) {
      const days = (new Date(endDate) - new Date(startDate)) / 86400000;
      params.group_by = days > 31 ? 'week' : 'day';
    }
    api.get('/api/analytics/counselor-performance', { params })
      .then((r) => setCounselorDetail(r.data.counselors || []))
      .catch(() => setCounselorDetail([]))
      .finally(() => setCounselorDetailLoading(false));
  }, [startDate, endDate]);

  const toggleCounselorRow = (counselorId) => {
    setExpandedCounselorId((prev) => (prev === counselorId ? null : counselorId));
    setCounselorPanelView('date');
  };

  useEffect(() => {
    Promise.all([
      api.get('/api/analytics/campaign-performance'),
      api.get('/api/analytics/platform-performance'),
    ]).then(([cp, pp]) => {
      setCampaignPerf(cp.data.campaigns || cp.data.rows || cp.data || []);
      setPlatformPerf(pp.data.platforms || pp.data.rows || pp.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setOolLoading(true);
    api.get('/api/analytics/out-of-location')
      .then((r) => setOutOfLocation(r.data.leads || r.data.rows || r.data || []))
      .catch(() => {})
      .finally(() => setOolLoading(false));
  }, []);

  const openFunnel = async (camp) => {
    const id = camp.id || camp._id || camp.campaign_id;
    setFunnelCampaign(camp);
    setFunnelData(null);
    setFunnelLoading(true);
    try {
      const { data } = await api.get(`/api/analytics/campaign-funnel/${id}`);
      setFunnelData(data);
    } catch {
      setFunnelData({ stages: [] });
    } finally {
      setFunnelLoading(false);
    }
  };

  const sortedCampaigns = useMemo(() => {
    const filtered = campaignPerf.filter((c) => campaignStatusFilter === 'all' || c.status === campaignStatusFilter);
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      const av = a[campaignSort.key] ?? 0;
      const bv = b[campaignSort.key] ?? 0;
      if (typeof av === 'string') return campaignSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return campaignSort.dir === 'asc' ? av - bv : bv - av;
    });
    return sorted;
  }, [campaignPerf, campaignStatusFilter, campaignSort]);

  const campaignSummary = useMemo(() => {
    const total = campaignPerf.length;
    const active = campaignPerf.filter((c) => c.status === 'active').length;
    const totalSpend = campaignPerf.reduce((s, c) => s + (Number(c.spend) || 0), 0);
    const totalRevenue = campaignPerf.reduce((s, c) => s + (Number(c.revenue) || 0), 0);
    const avgRoas = total > 0
      ? campaignPerf.reduce((s, c) => s + (Number(c.roas) || 0), 0) / total
      : 0;
    return { total, active, totalSpend, totalRevenue, avgRoas };
  }, [campaignPerf]);

  const toggleCampaignSort = (key) => {
    setCampaignSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleExport = (reportType) => {
    const params = new URLSearchParams();
    params.set('report_type', reportType);
    params.set('format', 'csv');
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    api.get(`/api/analytics/export?${params.toString()}`, { responseType: 'blob' }).then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}_report.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }).catch(console.error);
  };

  const sortedCounselors = useMemo(() => {
    if (!dash?.counselor_performance) return [];
    const sorted = [...dash.counselor_performance];
    sorted.sort((a, b) => {
      const aVal = a[counselorSort.key] ?? 0;
      const bVal = b[counselorSort.key] ?? 0;
      return counselorSort.dir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [dash?.counselor_performance, counselorSort]);

  const toggleSort = (key) => {
    setCounselorSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  const SortIcon = ({ col }) => {
    if (counselorSort.key !== col) return <CaretDown size={10} className="text-muted-foreground opacity-30" />;
    return counselorSort.dir === 'asc' ? <CaretUp size={10} className="text-foreground" /> : <CaretDown size={10} className="text-foreground" />;
  };

  const tt = { fontSize: 11, borderRadius: 8, fontFamily: 'DM Sans', background: theme === 'dark' ? '#1C1C1C' : '#fff', border: `1px solid ${theme === 'dark' ? '#2E2E2E' : '#E3E0DB'}`, color: theme === 'dark' ? '#EDEBE6' : '#191919', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' };
  const ax = theme === 'dark' ? '#555' : '#aaa';
  const grid = theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';

  if (loading) return <div className="text-sm text-muted-foreground text-center py-12 font-mono">loading reports...</div>;

  // Sidebar already hides this link for counselor/faculty -- defense-in-depth
  // check for anyone navigating here directly by URL.
  if (!['super_admin', 'branch_manager', 'front_desk', 'finance'].includes(user?.role)) {
    return (
      <div data-testid="reports-access-denied" className="text-sm text-muted-foreground text-center py-24">
        You don't have access to this page.
      </div>
    );
  }

  return (
    <div data-testid="reports-page" className="stagger-in">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">Organization-wide analytics</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <CalendarBlank size={14} className="text-muted-foreground" />
            <input
              data-testid="date-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-8 px-2 text-xs font-mono border border-border rounded-sm bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="Start date"
            />
            <span className="text-muted-foreground text-xs">&ndash;</span>
            <input
              data-testid="date-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-8 px-2 text-xs font-mono border border-border rounded-sm bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="End date"
            />
          </div>
          <button
            data-testid="export-dashboard-csv"
            onClick={() => handleExport('dashboard')}
            className="h-8 px-3 text-xs font-mono font-medium border border-border rounded-sm bg-background text-foreground hover:bg-secondary transition-colors flex items-center gap-1.5"
          >
            <Export size={13} />
            Export CSV
          </button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-secondary rounded-lg h-9">
          <TabsTrigger value="overview" data-testid="tab-report-overview" className="text-xs rounded-md font-mono">Overview</TabsTrigger>
          <TabsTrigger value="source" data-testid="tab-report-source" className="text-xs rounded-md font-mono">Sources</TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-report-finance" className="text-xs rounded-md font-mono">Finance</TabsTrigger>
          <TabsTrigger value="counselor" data-testid="tab-report-counselor" className="text-xs rounded-md font-mono">Counselors</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="tab-report-campaigns" className="text-xs rounded-md font-mono">Campaigns</TabsTrigger>
          <TabsTrigger value="ool" data-testid="tab-report-ool" className="text-xs rounded-md font-mono">Out of Location</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Total Leads" value={dash?.total_leads} accent="#2B6EF0" />
            <KPI label="Enrolled" value={dash?.enrolled_students} accent="#10A882" />
            <KPI label="Conversion" value={`${dash?.conversion_rate}%`} accent="#E04B2A" />
            <KPI label="Lost" value={dash?.lost_leads} accent="#EF4444" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <Card className="lg:col-span-3 border border-border rounded-xl"><CardContent className="p-5">
              <h3 className="font-display font-bold text-sm text-foreground mb-4">Leads by Source</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={dash?.leads_by_source} margin={{ left: -24, bottom: 30 }}>
                  <XAxis dataKey="source" tick={{ fontSize: 9, fill: ax, fontFamily: 'JetBrains Mono' }} angle={-35} textAnchor="end" />
                  <YAxis tick={{ fontSize: 9, fill: ax, fontFamily: 'JetBrains Mono' }} />
                  <Tooltip contentStyle={tt} />
                  <Bar dataKey="count" fill="#E04B2A" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent></Card>
            <Card className="lg:col-span-2 border border-border rounded-xl"><CardContent className="p-5">
              <h3 className="font-display font-bold text-sm text-foreground mb-4">Pipeline</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart><Pie data={dash?.leads_by_stage} dataKey="count" nameKey="stage" cx="50%" cy="50%" outerRadius={75} innerRadius={45} strokeWidth={0}>{dash?.leads_by_stage?.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Pie><Tooltip contentStyle={tt} formatter={(v, n) => [v, n.replace('_', ' ')]} /></PieChart>
              </ResponsiveContainer>
            </CardContent></Card>
          </div>
        </TabsContent>

        <TabsContent value="source" className="mt-4 space-y-4">
          <Card className="border border-border rounded-xl"><CardContent className="p-5">
            <h3 className="font-display font-bold text-sm text-foreground mb-4">Source Performance</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dash?.leads_by_source} margin={{ left: -24, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis dataKey="source" tick={{ fontSize: 9, fill: ax, fontFamily: 'JetBrains Mono' }} angle={-35} textAnchor="end" />
                <YAxis tick={{ fontSize: 9, fill: ax, fontFamily: 'JetBrains Mono' }} />
                <Tooltip contentStyle={tt} />
                <Bar dataKey="count" fill="#2B6EF0" radius={[4, 4, 0, 0]} name="Leads" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent></Card>

          {/* Source ROI Table */}
          <Card className="border border-border rounded-xl"><CardContent className="p-5">
            <h3 className="font-display font-bold text-sm text-foreground mb-4">Source ROI</h3>
            <div className="overflow-x-auto">
              <table data-testid="source-roi-table" className="w-full text-left">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2 pr-4 font-mono">Source</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2 pr-4 font-mono text-right">Total Leads</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2 pr-4 font-mono text-right">Enrolled</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2 pr-4 font-mono text-right">Conversion Rate</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2 font-mono text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {dash?.source_roi?.map((s) => (
                    <tr key={s.source} className="border-b border-border last:border-0">
                      <td className="text-[13px] text-foreground font-medium py-2.5 pr-4">{s.source}</td>
                      <td className="text-[12px] text-muted-foreground font-mono py-2.5 pr-4 text-right">{s.total_leads}</td>
                      <td className="text-[12px] font-mono py-2.5 pr-4 text-right" style={{ color: '#10A882' }}>{s.enrolled}</td>
                      <td className="text-[12px] text-foreground font-mono font-semibold py-2.5 pr-4 text-right">{s.conversion_rate}%</td>
                      <td className="text-[12px] text-foreground font-mono py-2.5 text-right">{fmt(s.revenue)}</td>
                    </tr>
                  ))}
                  {(!dash?.source_roi || dash.source_roi.length === 0) && (
                    <tr><td colSpan={5} className="text-xs text-muted-foreground py-4 text-center">No data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="finance" className="mt-4 space-y-4">
          <div className="flex justify-end mb-1">
            <button
              data-testid="export-finance-csv"
              onClick={() => handleExport('finance')}
              className="h-8 px-3 text-xs font-mono font-medium border border-border rounded-sm bg-background text-foreground hover:bg-secondary transition-colors flex items-center gap-1.5"
            >
              <Export size={13} />
              Export Finance CSV
            </button>
          </div>
          {fin && (<>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="Billed" value={fmt(fin.total_amount)} accent="#2B6EF0" />
              <KPI label="Collected" value={fmt(fin.collected_amount)} accent="#10A882" />
              <KPI label="Outstanding" value={fmt(fin.outstanding)} accent="#EF4444" />
              <KPI label="Rate" value={`${fin.collection_rate}%`} accent="#E5A100" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="border border-border rounded-xl"><CardContent className="p-5">
                <h3 className="font-display font-bold text-sm text-foreground mb-4">Invoice Status</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart><Pie data={[{ name: 'Paid', value: fin.paid_count }, { name: 'Partial', value: fin.partial_count }, { name: 'Pending', value: fin.pending_count }, { name: 'Overdue', value: fin.overdue_count }]} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40} strokeWidth={0}><Cell fill="#10A882" /><Cell fill="#E5A100" /><Cell fill="#2B6EF0" /><Cell fill="#EF4444" /></Pie><Tooltip contentStyle={tt} /></PieChart>
                </ResponsiveContainer>
              </CardContent></Card>
              <Card className="border border-border rounded-xl"><CardContent className="p-5">
                <h3 className="font-display font-bold text-sm text-foreground mb-4">Payment Methods</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={fin.payment_methods}><XAxis dataKey="method" tick={{ fontSize: 9, fill: ax, fontFamily: 'JetBrains Mono' }} /><YAxis tick={{ fontSize: 9, fill: ax, fontFamily: 'JetBrains Mono' }} /><Tooltip contentStyle={tt} formatter={(v) => fmt(v)} /><Bar dataKey="total" fill="#2B6EF0" radius={[4, 4, 0, 0]} name="Amount" /></BarChart>
                </ResponsiveContainer>
              </CardContent></Card>
            </div>
          </>)}
        </TabsContent>

        <TabsContent value="counselor" className="mt-4">
          <Card className="border border-border rounded-xl"><CardContent className="p-5">
            <h3 className="font-display font-bold text-sm text-foreground mb-4">Counselor Performance</h3>
            <div className="overflow-x-auto">
              <table data-testid="counselor-table" className="w-full text-left">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2 pr-4 font-mono w-8">#</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2 pr-4 font-mono">Name</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2 pr-4 font-mono text-right cursor-pointer select-none" onClick={() => toggleSort('total_leads')}>
                      <span className="inline-flex items-center gap-1">Leads Assigned <SortIcon col="total_leads" /></span>
                    </th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2 pr-4 font-mono text-right cursor-pointer select-none" onClick={() => toggleSort('enrolled')}>
                      <span className="inline-flex items-center gap-1">Enrolled <SortIcon col="enrolled" /></span>
                    </th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2 font-mono text-right cursor-pointer select-none" onClick={() => toggleSort('conversion_rate')}>
                      <span className="inline-flex items-center gap-1">Conversion Rate <SortIcon col="conversion_rate" /></span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCounselors.map((c, i) => {
                    const isOpen = expandedCounselorId === c.counselor_id;
                    const detail = counselorDetail.find((d) => d.counselor_id === c.counselor_id);
                    return (
                      <Fragment key={c.counselor_id}>
                        <tr
                          data-testid={`counselor-row-${c.counselor_id}`}
                          className="border-b border-border last:border-0 hover:bg-secondary/50 transition-colors cursor-pointer"
                          onClick={() => toggleCounselorRow(c.counselor_id)}
                        >
                          <td className="text-[10px] text-muted-foreground font-mono py-2.5 pr-4">{i + 1}</td>
                          <td className="py-2.5 pr-4">
                            <div className="flex items-center gap-2.5">
                              {isOpen ? <CaretUp size={10} className="text-muted-foreground" /> : <CaretDown size={10} className="text-muted-foreground opacity-40" />}
                              <div className="w-7 h-7 rounded-sm bg-secondary flex items-center justify-center text-[10px] font-bold text-muted-foreground font-display">{c.name?.charAt(0)}</div>
                              <span className="text-[13px] text-foreground font-medium">{c.name}</span>
                            </div>
                          </td>
                          <td className="text-[12px] text-muted-foreground font-mono py-2.5 pr-4 text-right">{c.total_leads}</td>
                          <td className="text-[12px] font-mono py-2.5 pr-4 text-right" style={{ color: '#10A882' }}>{c.enrolled}</td>
                          <td className="text-[12px] text-foreground font-mono font-semibold py-2.5 text-right">{c.conversion_rate}%</td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-secondary/20" data-testid={`counselor-expand-${c.counselor_id}`}>
                            <td colSpan={5} className="py-3 px-4">
                              <CounselorDetailPanel
                                loading={counselorDetailLoading}
                                detail={detail}
                                view={counselorPanelView}
                                setView={setCounselorPanelView}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {sortedCounselors.length === 0 && (
                    <tr><td colSpan={5} className="text-xs text-muted-foreground py-4 text-center">No data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="campaigns" className="mt-4 space-y-4" data-testid="campaigns-tab">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI label="Total Campaigns" value={campaignSummary.total} accent="#2B6EF0" />
            <KPI label="Active" value={campaignSummary.active} accent="#10A882" />
            <KPI label="Total Spend" value={fmt(campaignSummary.totalSpend)} accent="#E5A100" />
            <KPI label="Total Revenue" value={fmt(campaignSummary.totalRevenue)} accent="#E04B2A" />
            <KPI label="Avg ROAS" value={`${campaignSummary.avgRoas.toFixed(2)}x`} accent="#8B5CF6" />
          </div>

          {/* Platform breakdown */}
          {platformPerf.length > 0 && (
            <Card className="border border-border rounded-xl"><CardContent className="p-5">
              <h3 className="font-display font-bold text-sm text-foreground mb-4">Platform Breakdown (Leads by Source)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={platformPerf}
                    dataKey="leads"
                    nameKey="source"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    innerRadius={45}
                    strokeWidth={0}
                  >
                    {platformPerf.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tt} />
                  <Legend wrapperStyle={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent></Card>
          )}

          {/* Filter & Table */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Status:</span>
            {['all', 'active', 'paused', 'completed', 'draft'].map((s) => (
              <button
                key={s}
                data-testid={`report-camp-status-${s}`}
                onClick={() => setCampaignStatusFilter(s)}
                className="text-[11px] px-2.5 py-1 rounded-md font-semibold uppercase tracking-wider transition-colors border border-border"
                style={{
                  backgroundColor: campaignStatusFilter === s ? '#E04B2A' : 'transparent',
                  color: campaignStatusFilter === s ? '#fff' : 'hsl(var(--muted-foreground))',
                  borderColor: campaignStatusFilter === s ? '#E04B2A' : 'hsl(var(--border))',
                }}
              >
                {s}
              </button>
            ))}
          </div>

          <Card className="border border-border rounded-xl"><CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="campaign-perf-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono cursor-pointer" onClick={() => toggleCampaignSort('name')}>Name</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono">Source</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono">Status</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono text-right cursor-pointer" onClick={() => toggleCampaignSort('spend')}>Spend</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono text-right cursor-pointer" onClick={() => toggleCampaignSort('leads')}>Leads</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono text-right cursor-pointer" onClick={() => toggleCampaignSort('enrolled')}>Enrolled</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono text-right cursor-pointer" onClick={() => toggleCampaignSort('cpl')}>CPL</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono text-right cursor-pointer" onClick={() => toggleCampaignSort('revenue')}>Revenue</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono text-right cursor-pointer" onClick={() => toggleCampaignSort('roas')}>ROAS</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCampaigns.length === 0 ? (
                    <tr><td colSpan={9} className="text-xs text-muted-foreground py-6 text-center">No campaign data</td></tr>
                  ) : sortedCampaigns.map((c) => {
                    const id = c.id || c._id || c.campaign_id;
                    return (
                      <tr key={id} data-testid={`camp-perf-row-${id}`} className="border-b border-border last:border-0 cursor-pointer hover:bg-secondary/40 transition-colors" onClick={() => openFunnel(c)}>
                        <td className="py-2.5 px-4 text-[12px] text-foreground font-medium">{c.name || '-'}</td>
                        <td className="py-2.5 px-4 text-[11px] text-muted-foreground font-mono">{c.source || '-'}</td>
                        <td className="py-2.5 px-4 text-[10px] uppercase font-bold tracking-wider" style={{ color: c.status === 'active' ? '#10A882' : c.status === 'paused' ? '#E5A100' : '#808080' }}>{c.status || 'draft'}</td>
                        <td className="py-2.5 px-4 text-right text-[11px] text-foreground font-mono">{fmt(c.spend)}</td>
                        <td className="py-2.5 px-4 text-right text-[11px] text-foreground font-mono">{(Number(c.leads) || 0).toLocaleString('en-IN')}</td>
                        <td className="py-2.5 px-4 text-right text-[11px] font-mono" style={{ color: '#10A882' }}>{(Number(c.enrolled) || 0).toLocaleString('en-IN')}</td>
                        <td className="py-2.5 px-4 text-right text-[11px] text-foreground font-mono">{fmt(c.cpl)}</td>
                        <td className="py-2.5 px-4 text-right text-[11px] text-foreground font-mono">{fmt(c.revenue)}</td>
                        <td className="py-2.5 px-4 text-right text-[11px] font-mono font-semibold" style={{ color: (Number(c.roas) || 0) >= 1 ? '#10A882' : '#EF4444' }}>{(Number(c.roas) || 0).toFixed(2)}x</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="ool" className="mt-4 space-y-4" data-testid="ool-tab">
          <div>
            <h2 className="font-display text-xl font-bold text-foreground">Out-of-Location Leads</h2>
            <p className="text-xs text-muted-foreground mt-1">Leads from cities that don't match their assigned branch</p>
          </div>
          <Card className="border border-border rounded-xl overflow-hidden">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E5A10015', color: '#E5A100' }}>
                <MapPin size={18} weight="fill" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Total</p>
                <p className="font-display text-2xl font-extrabold text-foreground">
                  <span className="font-mono">{outOfLocation.length}</span>
                  <span className="text-xs text-muted-foreground font-sans ml-2">leads are out of location</span>
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border rounded-xl"><CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="ool-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono">Student</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono">Lead City</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono">Branch City</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono">Source</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono">Stage</th>
                    <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {oolLoading ? (
                    <tr><td colSpan={6} className="text-xs text-muted-foreground py-6 text-center font-mono">loading...</td></tr>
                  ) : outOfLocation.length === 0 ? (
                    <tr><td colSpan={6} className="text-xs text-muted-foreground py-6 text-center">No out-of-location leads</td></tr>
                  ) : outOfLocation.map((l) => {
                    const id = l.id || l._id || l.lead_id;
                    return (
                      <tr key={id} data-testid={`ool-row-${id}`} className="border-b border-border last:border-0 cursor-pointer hover:bg-secondary/40 transition-colors" onClick={() => navigate(`/leads/${id}`)}>
                        <td className="py-2.5 px-4 text-[12px] text-foreground font-medium">{l.student_name || '-'}</td>
                        <td className="py-2.5 px-4 text-[11px] text-foreground font-mono">{l.lead_city || l.city || '-'}</td>
                        <td className="py-2.5 px-4 text-[11px] text-muted-foreground font-mono">{l.branch_city || '-'}</td>
                        <td className="py-2.5 px-4 text-[11px] text-muted-foreground font-mono">{l.source || '-'}</td>
                        <td className="py-2.5 px-4 text-[10px] uppercase font-bold tracking-wider text-foreground/80">{(l.stage || '').replace('_', ' ')}</td>
                        <td className="py-2.5 px-4 text-[10px] text-muted-foreground font-mono">{l.created_at ? new Date(l.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Funnel modal */}
      {funnelCampaign && (
        <FunnelModal
          campaign={funnelCampaign}
          data={funnelData}
          loading={funnelLoading}
          onClose={() => { setFunnelCampaign(null); setFunnelData(null); }}
        />
      )}
    </div>
  );
}

function FunnelModal({ campaign, data, loading, onClose }) {
  const stages = data?.stages || data?.funnel || [];
  const max = stages.reduce((m, s) => Math.max(m, Number(s.count) || 0), 0) || 1;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="funnel-modal" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl w-full max-w-2xl p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-lg font-bold text-foreground">{campaign.name}</h2>
            <p className="text-[11px] text-muted-foreground font-mono">{campaign.source} &middot; Funnel by stage</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        {loading ? (
          <div className="text-xs text-muted-foreground text-center py-12 font-mono">loading funnel...</div>
        ) : stages.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-12">No funnel data</div>
        ) : (
          <div className="space-y-2">
            {stages.map((s, i) => {
              const count = Number(s.count) || 0;
              const pct = (count / max) * 100;
              return (
                <div key={i} data-testid={`funnel-stage-${s.stage || i}`} className="space-y-1">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-foreground font-medium uppercase tracking-wider">{(s.stage || s.name || '').replace('_', ' ')}</span>
                    <span className="font-mono text-muted-foreground">{count}</span>
                  </div>
                  <div className="h-5 rounded-sm bg-secondary overflow-hidden">
                    <div className="h-full rounded-sm transition-all" style={{ width: `${pct}%`, backgroundColor: '#E04B2A' }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function CounselorDetailPanel({ loading, detail, view, setView }) {
  if (loading) {
    return <div className="text-[11px] text-muted-foreground font-mono py-2">loading…</div>;
  }
  if (!detail) {
    return <div className="text-[11px] text-muted-foreground py-2">No data</div>;
  }
  const byDate = detail.by_date || [];
  const byCourse = detail.by_course || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5">
        {['date', 'course'].map((v) => (
          <button
            key={v}
            type="button"
            data-testid={`counselor-panel-view-${v}`}
            onClick={() => setView(v)}
            className="text-[10px] px-2.5 py-1 rounded-md font-semibold uppercase tracking-wider transition-colors border border-border"
            style={{
              backgroundColor: view === v ? '#E04B2A' : 'transparent',
              color: view === v ? '#fff' : 'hsl(var(--muted-foreground))',
              borderColor: view === v ? '#E04B2A' : 'hsl(var(--border))',
            }}
          >
            By {v === 'date' ? 'Date' : 'Course'}
          </button>
        ))}
      </div>

      {view === 'date' ? (
        byDate.length === 0 ? (
          <div className="text-[11px] text-muted-foreground py-1">No enquiries in range</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="counselor-by-date-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold py-1.5 pr-4 font-mono">Date</th>
                  <th className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold py-1.5 pr-4 font-mono text-right">Enquiries</th>
                  <th className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold py-1.5 font-mono text-right">Enrolled</th>
                </tr>
              </thead>
              <tbody>
                {byDate.map((d) => (
                  <tr key={d.date} className="border-b border-border last:border-0">
                    <td className="text-[11px] text-foreground font-mono py-1.5 pr-4">{d.date}</td>
                    <td className="text-[11px] text-muted-foreground font-mono py-1.5 pr-4 text-right">{d.enquiries}</td>
                    <td className="text-[11px] font-mono py-1.5 text-right" style={{ color: '#10A882' }}>{d.enrolled}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : byCourse.length === 0 ? (
        <div className="text-[11px] text-muted-foreground py-1">No enquiries in range</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="counselor-by-course-table">
            <thead>
              <tr className="border-b border-border">
                <th className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold py-1.5 pr-4 font-mono">Course</th>
                <th className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold py-1.5 pr-4 font-mono text-right">Enquiries</th>
                <th className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold py-1.5 pr-4 font-mono text-right">Enrolled</th>
                <th className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold py-1.5 font-mono text-right">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {byCourse.map((c) => (
                <tr key={c.course_id} className="border-b border-border last:border-0">
                  <td className="text-[11px] text-foreground font-medium py-1.5 pr-4">{c.course_name}</td>
                  <td className="text-[11px] text-muted-foreground font-mono py-1.5 pr-4 text-right">{c.enquiries}</td>
                  <td className="text-[11px] font-mono py-1.5 pr-4 text-right" style={{ color: '#10A882' }}>{c.enrolled}</td>
                  <td className="text-[11px] text-foreground font-mono font-semibold py-1.5 text-right">{c.conversion_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <Card className="border border-border rounded-xl overflow-hidden"><CardContent className="p-4 relative">
      <div className="absolute top-0 left-0 w-full h-[3px]" style={{ backgroundColor: accent }} />
      <p className="text-[9px] text-muted-foreground uppercase tracking-[0.1em] font-bold mt-1">{label}</p>
      <p className="font-display text-xl font-extrabold text-foreground mt-1">{value}</p>
    </CardContent></Card>
  );
}
